/**
 * 呼叫中心
 *
 */

import { defineStore } from 'pinia'
import {
  callCenterStatusEnum,
  seatStatusEnum,
  mutedStatus,
  dialingScenes,
  CallingCode,
  callResult,
  callType,
  callDirection
} from '@/enums/callCenter'
import { useJsSIP } from '@/hooks/callCenter/jssip'
import type {
  CallCenterAddCustomVO,
  CallCenterConfig,
  CallType,
  CallUserInfoVO,
  HeartBeatData,
  LineInfo,
  RoundCallTaskInfo,
  UseCallCenterVO
} from '@/types/callCenter/callCenter'
import { ref } from 'vue'
import {
  queryOrUpdateCallType,
  queryCurrentCallCenterConf,
  getDefaultSipLine,
  setDefaultCallTypeFirst
} from '@/apis/callCenter/callCenter'
import { useDaco } from '@/hooks/callCenter/daco'
import { isNullOrUndefOrEmpty } from '@/utils/is'
import { heartBeatReport } from '@/apis/callCenter/cloudGroupCall'
import { queryPcEvokeMobile } from '@/apis/callCenter/callCenter'
const useJsSIPHooks = ref()
const useDacoHooks = ref()

export const useCallCenter = defineStore({
  id: 'callCenter',
  state: (): UseCallCenterVO => {
    return {
      callCenterConfig: {} as CallCenterConfig,
      callCenterStatus: callCenterStatusEnum.READY,
      callSeatStatus: seatStatusEnum.LEAVE,
      mutedStatus: mutedStatus.unmute,
      showCallCenter: false,
      dialingScenes: dialingScenes.singleCall,
      callingCode: CallingCode.CORG,
      callResult: callResult.unConnected,
      callInResult: callResult.unConnected,
      staffDefaultCallType: callType.sip,
      callSreverType: [],
      showCallScreen: false,
      callDirection: callDirection.out,
      callUserInfo: {},
      callAddCustom: false,
      callIntervalTime: 10,
      notCountDownFlag: 0,
      usbStatus: false,
      relateScenes: 0,
      floatMenuVisible: false,
      callWriteFollow: false,
      isCheckPhoneDetail: false,
      quickCallAdd: false,
      callWriteFollowTag: false,
      callCenterAddCustomData: {},
      callType: 1,
      showCpsCallRecord: false,
      cpsShopProductId: '',
      showRecordPlayer: false,
      recordPlayerData: {},
      recordUrl: '',
      reportSeatStatusTimer: null,
      currentEvokeSwitch: null,
      mobileOrWorkDevice: null,
      hasWorkMachine: false
    }
  },
  actions: {
    // 初始化呼叫中心
    async initCallCenter() {
      useJsSIPHooks.value = useJsSIP()
      useDacoHooks.value = useDaco()
      // 初始化之前先关闭连接
      useDacoHooks.value.oncloseWS()
      useJsSIPHooks.value.jssipStop()
      this.showCallScreen = false
      this.floatMenuVisible = false
      this.dialingScenes = dialingScenes.singleCall
      // console.log('初始化呼叫中心', this.callCenterConfig)
      ;(await this.getCurrentCallCenterConf()) as CallCenterConfig
      if (this.callCenterConfig?.callSwitch === 1 && this.callCenterConfig.seatSwitch === 1) {
        this.showCallCenter = true
        await this.getDefaultCallType()

        // 获取电脑唤起手机拨打开关
        this.getQueryPcEvokeMobile()

        // 查询默认呼叫方式
        await this.getCallType()
        // 服务过期不初始化,上传坐席状态
        if (this.callCenterConfig?.seatExpired) {
          this.reportSeatStatus({
            status: seatStatusEnum.LEAVE,
            viewSeatStatus: callCenterStatusEnum.EXPIRED
          } as HeartBeatData)
          return false
        }
        if (
          this.staffDefaultCallType === callType.noset ||
          this.staffDefaultCallType === callType.app
        ) {
          this.setCallCenterStatus(
            this.staffDefaultCallType === callType.noset
              ? callCenterStatusEnum.STOP
              : callCenterStatusEnum.READY
          )
        }
        this.handleCallType()
      } else {
        // 不显示电话条
        this.showCallCenter = false
      }
    },

    handleCallType() {
      if (this.staffDefaultCallType === callType.sip) {
        this.initSip()
      }
      if (this.staffDefaultCallType === callType.daco) {
        this.initDoca()
      }
    },

    // 初始化软件电话
    initSip() {
      useJsSIPHooks.value.createJssipSocket(this.callCenterConfig?.sipInfo)
    },
    // 初始化多卡话机
    initDoca() {
      useDacoHooks.value.createCallCenterSocket()
    },
    // 获取呼叫设置配置
    getCallType() {
      return new Promise((resolve, reject) => {
        queryOrUpdateCallType()
          .then((res: CallType) => {
            this.staffDefaultCallType = res.staffDefaultCallType
            this.organCallTypes = res.organCallTypes
            resolve(res)
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    getQueryPcEvokeMobile() {
      queryPcEvokeMobile().then((res: any) => {
        this.currentEvokeSwitch = res.currentEvokeSwitch
        this.mobileOrWorkDevice = res.mobileOrWorkDevice
      })
    },
    // 获取用户当前呼叫中心配置
    getCurrentCallCenterConf(): Promise<CallCenterConfig> {
      return new Promise((resolve, reject) => {
        queryCurrentCallCenterConf()
          .then((res: CallCenterConfig) => {
            this.callCenterConfig = res
            resolve(res)
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    // 设置呼叫中心状态
    setCallCenterStatus(value: any) {
      this.callCenterStatus = value
    },
    // 设置坐席状态
    setCallSeatStatus(value: number) {
      this.callSeatStatus = value
    },
    // 设置静音状态
    setMutedStatus(value: string) {
      this.mutedStatus = value
    },
    // 设置呼叫中心电话条是否展示
    setShowCallCenter(value: boolean) {
      this.showCallCenter = value
    },
    // 设置拨叫场景
    setdialingScenes(value: number) {
      this.dialingScenes = value
    },
    // 设置呼叫流程code
    setCallingCode(value: string) {
      this.callingCode = value
    },
    // 设置呼叫结果
    setCallResult(value: number) {
      this.callResult = value
    },
    // 设置呼入结果
    setCallInResult(value: number) {
      this.callInResult = value
    },
    // 修改呼叫方式
    setStaffDefaultCallType(value: number) {
      this.staffDefaultCallType = value
    },

    // 电脑唤起手机拨打开关 0-关 1-开
    setCurrentEvokeSwitch(value: any) {
      this.currentEvokeSwitch = value
    },

    // 电脑唤起手机拨打开关 0-关 1-开
    setMobileOrWorkDevice(value: any) {
      this.mobileOrWorkDevice = value
    },

    // 上报坐席状态
    heartBeatReportSeatStatus() {
      this.reportSeatStatusTimer = setInterval(async () => {
        this.callCenterConfig = (await this.getCurrentCallCenterConf()) as CallCenterConfig
        // 如果坐席过期，上报坐席过期
        console.log('%c自动上报', 'color: red;font-size: 5em')
        heartBeatReport({
          status: this.callCenterConfig.seatExpired ? seatStatusEnum.LEAVE : this.callSeatStatus,
          viewSeatStatus: this.callCenterConfig.seatExpired
            ? callCenterStatusEnum.EXPIRED
            : this.callCenterStatus,
          sign: '自动上报'
        })
      }, 30000)
    },
    /**
     * 更新坐席状态
     */
    reportSeatStatus(data: HeartBeatData) {
      // console.log('%c主动上报', 'color: red;font-size: 5em', data.status, data.viewSeatStatus)
      heartBeatReport({
        status: data.status,
        viewSeatStatus: data.viewSeatStatus,
        sign: '主动上报'
      }).then(() => {
        this.callSeatStatus = data.status
        this.callCenterStatus = data.viewSeatStatus
      })
    },
    // 设置呼叫服务类型
    setCallServerType(value: string[]) {
      this.callSreverType = value
    },
    /**
     * 设置通话弹屏
     */
    setShowCallScreen(value: boolean) {
      this.showCallScreen = value
    },
    /**
     * 设置新建客户
     */
    setCallAddCustom(value: boolean) {
      this.callAddCustom = value
    },
    /**
     * 用户信息
     */
    setCallUserInfo(user: CallUserInfoVO) {
      this.callUserInfo = user
      console.log(user, '呼出用户信息')
    },
    /**
     * 呼入用户信息
     */
    setCallUserInfoIn(user: CallUserInfoVO) {
      this.callUserInfoIn = user
      console.log(user, '呼入用户信息')
    },
    /**
     * 设置轮呼倒计时
     */
    setCallIntervalTime(time: number) {
      this.callIntervalTime = time
    },
    /**
     * 设置轮呼是否需要倒计时
     */
    setNotCountDownFlag(flag: number) {
      this.notCountDownFlag = flag
    },
    /**
     * 设置呼叫方向
     */
    setCallDirection(value: number) {
      this.callDirection = value
    },
    /**
     * 设置imei
     */
    setIMEI(value: string) {
      this.imei = value
    },
    /**
     * 设置iccid
     */
    setIccid(value: string) {
      this.iccid = value
    },
    /**
     * 设置多卡话机usb连接状态
     */
    setUsbStatus(value: boolean) {
      this.usbStatus = value
    },
    /**
     * 设置话机sim卡
     */
    setSimList(value: string) {
      this.simList = value
      // "0,1,1,0,0,3,0"
      // Sim1-5 位：对应 1-5 卡槽。0 代表未插卡，1 已插卡 第六位 当前卡槽simIndex  第七位 0:注册成功 1：搜索中 2：注网失败 3：没有卡 4：紧急呼叫
      const simList: Recordable[] = [] // 原始数据
      const filterSimList: { value: string; index: number }[] = [] // 过滤后的simList 只需要前五
      let changeSimList = [] // 可切换的卡槽list
      const currentData = value?.split?.(',') || []
      currentData.map((item, index) => {
        simList.push({
          value: item,
          index: index + 1
        })
        if (index < 5) {
          filterSimList.push({
            value: item,
            index: index + 1
          })
        }
      })
      // 当前使用simIndex
      const currentSimIndex = simList?.[5]?.value
      this.simIndex = currentSimIndex
      changeSimList = filterSimList.filter(
        item => item.value === '1' && Number(item.index) !== Number(currentSimIndex)
      )
      console.log(changeSimList, 'changeSimList')
      this.changeSimList = changeSimList
    },
    /**
     * 设置smi卡号码
     */
    setCallerPhone(value: string) {
      this.callerPhone = value
    },
    /**
     * 设置callId
     */
    setCallId(value: string) {
      this.callId = value
    },
    /**
     * 设置呼入callId
     */
    setCallInId(value: string) {
      this.callInId = value
    },
    /**
     * 设置线路信息
     */
    setlineInfo(value: LineInfo) {
      this.lineInfo = value
    },
    /**
     * 获取默认线路
     */
    getDefaultSipLine() {
      return new Promise<LineInfo>((resolve, reject) => {
        getDefaultSipLine()
          .then(res => {
            if (!isNullOrUndefOrEmpty(res.defaultLineId)) {
              const item = res?.lineList?.find((item: Recordable) => {
                return item.lineId === res.defaultLineId
              })
              // 存储线路信息
              this.setlineInfo({
                ...(item || {}),
                allUsedFlag: res?.allUsedFlag
              })
            } else {
              // 存储线路信息
              this.setlineInfo({
                ...(res?.lineList?.[0] || {}),
                allUsedFlag: res?.allUsedFlag
              })
            }
            resolve(res)
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    /**
     * 获取员工呼叫中心配置
     */
    getDefaultCallType() {
      return new Promise((resolve, reject) => {
        setDefaultCallTypeFirst()
          .then(res => {
            // console.log(res)
            if (res.staffDefaultCallType === callType.sip) {
              res.lineList?.length && this.setlineInfo({ ...res.lineList[0], allUsedFlag: null })
            }
            this.staffDefaultCallType = res.staffDefaultCallType
            this.organCallTypes = res.organCallTypes
            resolve(res)
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    /**
     * 设置sip线路callId
     */
    setJssipCallId(value: string) {
      this.jssipCallId = value
    },
    /**
     * 设置关联场景
     */
    setRelateScenes(value: number) {
      this.relateScenes = value
    },
    /**
     * 设置悬浮窗
     */
    setFloadMenu(value: boolean) {
      this.floatMenuVisible = value
    },
    /**
     * 设置写跟进状态
     */
    setCallWriteFollow(value: boolean) {
      this.callWriteFollow = value
    },
    setCallNumberIn(value: string) {
      this.callNumberIn = value
    },
    setCallNumberOut(value: string) {
      this.callNumberOut = value
    },
    /**
     * 设置群呼来电的businessId
     */
    setBizId(value: string) {
      this.bizId = value
    },
    /**
     * 设置轮呼任务信息
     */
    setRoundCallTaskInfo(data: RoundCallTaskInfo) {
      this.roundCallTaskInfo = data
    },
    /**
     * 设置群呼客户
     */
    setGroupCallUserInfo(data: CallUserInfoVO) {
      console.log('设置群呼客户', data)
      this.groupCallUserInfo = data
    },
    /*
     * 设置多卡话机usb连接状态
     */
    setIsCheckPhoneDetail(value: boolean) {
      this.isCheckPhoneDetail = value
    },
    /**
     * 快速拨号新建状态
     */
    setQuickCallAdd(value: boolean) {
      this.quickCallAdd = value
    },
    /**
     * 设置呼叫中心新增客户姓名电话
     */
    setCallCenterAddCustomData(data: CallCenterAddCustomVO) {
      this.callCenterAddCustomData = data
    },
    /**
     * 跟进记录是否已填写
     */
    setCallWriteFollowTag(value: boolean) {
      this.callWriteFollowTag = value
    },
    setCallType(value: number) {
      this.callType = value
    },
    // showCpsCallRecord
    setShowCpsCallRecord(value: boolean) {
      this.showCpsCallRecord = value
    },
    setCpsShopProductId(value: string) {
      this.cpsShopProductId = value
    },
    setShowRecordPlayer(value: boolean) {
      this.showRecordPlayer = value
    },
    setRecordPlayerData(data: Recordable) {
      this.recordPlayerData = data
    },
    setRecordUrl(value: string) {
      this.recordUrl = value
    }
  }
})
